<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import BaseLabel from '@/atoms/BaseLabel.vue';
import BaseCheckbox from '@/atoms/BaseCheckbox.vue';
import RowItemButton from '@/molecules/RowItemButton.vue';
import RowItemContainer from '@/organisms/RowItemContainer.vue';
import TextInput from '@/molecules/TextInput.vue';
import { REQUIRED_ERROR_MESSAGE } from '@/helpers/default-validators';

type Specialist = {
  name: string;
  value: string;
};

const props = defineProps<{
  specialists: Specialist[];
  label?: string;
  submitAttempted?: boolean;
}>();

const emit = defineEmits<{ canSubmit: [boolean] }>();

const modelValue = defineModel<string>({ default: '' });

const canSubmit = computed(() => !!modelValue.value?.length);

watch(canSubmit, (val) => emit('canSubmit', val), { immediate: true });

const isAutreSpecialist = (s: Specialist) => s.value.toLowerCase() === 'autre';
const isNoneOption = (s: Specialist) => s.value.toLowerCase() === 'aucun';

const normalSpecialists = computed(() => props.specialists.filter((s) => !isAutreSpecialist(s)));
const autreSpecialist = computed(() => props.specialists.find((s) => isAutreSpecialist(s)));

const isAutreChecked = ref(false);
const autreCustomValue = ref('');

onMounted(() => {
  if (!modelValue.value) return;
  const vals = modelValue.value
    .split(';')
    .map((v) => v.trim())
    .filter(Boolean);
  const knownVals = normalSpecialists.value.map((s) => s.value);
  const customVal = vals.find((v) => !knownVals.includes(v));
  if (customVal) {
    isAutreChecked.value = true;
    autreCustomValue.value = customVal;
  }
});

const knownSelected = computed((): string[] => {
  if (!modelValue.value) return [];
  const knownVals = normalSpecialists.value.map((s) => s.value);
  return modelValue.value
    .split(';')
    .map((v) => v.trim())
    .filter((v) => v && knownVals.includes(v));
});

const rebuildModel = (selected: string[], autreVal: string) => {
  const parts = [...selected];
  if (autreVal.trim()) parts.push(autreVal.trim());
  modelValue.value = parts.join(';');
};

const internalValues = computed({
  get: () => knownSelected.value,
  set: (vals: string[] | boolean | undefined) => {
    if (!Array.isArray(vals)) return;

    const justAddedNone = vals
      .filter((v) => normalSpecialists.value.some((s) => s.value === v && isNoneOption(s)))
      .find((v) => !knownSelected.value.includes(v));

    if (justAddedNone) {
      isAutreChecked.value = false;
      autreCustomValue.value = '';
      rebuildModel([justAddedNone], '');
      return;
    }

    const hadNone = knownSelected.value.some((v) =>
      normalSpecialists.value.some((s) => s.value === v && isNoneOption(s)),
    );
    const justAddedRegular = vals.some((v) => !knownSelected.value.includes(v));

    if (hadNone && justAddedRegular) {
      const withoutNone = vals.filter(
        (v) => !normalSpecialists.value.some((s) => s.value === v && isNoneOption(s)),
      );
      rebuildModel(withoutNone, isAutreChecked.value ? autreCustomValue.value : '');
      return;
    }

    rebuildModel(vals, isAutreChecked.value ? autreCustomValue.value : '');
  },
});

const toggleAutre = (checked: boolean) => {
  isAutreChecked.value = checked;
  if (checked) {
    const noneRemoved = knownSelected.value.filter(
      (v) => !normalSpecialists.value.some((s) => s.value === v && isNoneOption(s)),
    );
    rebuildModel(noneRemoved, autreCustomValue.value);
  } else {
    autreCustomValue.value = '';
    rebuildModel(knownSelected.value, '');
  }
};

watch(autreCustomValue, (val) => {
  rebuildModel(knownSelected.value, val);
});

const rowItems = computed(() =>
  normalSpecialists.value.map((item, index) => ({
    id: item.value,
    title: item.name,
    type: 'default',
    value: item.value,
    checked: internalValues.value.includes(item.value),
    class: 'py-7',
    // RowItemContainer retire la bordure du dernier item ; "Autre" est rendu
    // séparément juste après, il faut donc la forcer pour garder un séparateur.
    ...(autreSpecialist.value && index === normalSpecialists.value.length - 1
      ? { bordered: true }
      : {}),
  })),
);
</script>

<template>
  <div class="flex flex-col gap-2 mb-6">
    <BaseLabel :label="label || 'Spécialistes consultés'" />

    <RowItemContainer
      v-model="internalValues"
      :row-items="rowItems"
      class="text-primary border-none"
      rowItemType="CheckboxRowItem"
    />

    <!-- Option Autre -->
    <template v-if="autreSpecialist">
      <RowItemButton
        :id="autreSpecialist.value"
        :selected="isAutreChecked"
        :title="autreSpecialist.name"
        :bordered="false"
        type="default"
        class="py-7"
        @click="toggleAutre(!isAutreChecked)"
      >
        <BaseCheckbox id="autre-check" v-model="isAutreChecked" color-mode="on-brand" />
      </RowItemButton>

      <div v-if="isAutreChecked" class="px-1 mt-1">
        <TextInput
          :id="autreSpecialist.value"
          v-model="autreCustomValue"
          placeholder="Précisez le professionnel de santé..."
        />
      </div>
    </template>

    <BaseLabel
      v-if="submitAttempted && !canSubmit"
      :text="REQUIRED_ERROR_MESSAGE"
      color="error"
      preset="small"
    />
  </div>
</template>
